import { z } from 'zod';

export const OBJECTIVE_TYPES = [
  'inquiry',
  'document_request',
  'reservation',
  'application',
  'purchase',
  'recruitment',
] as const;

export const INDUSTRY_TYPES = [
  'b2b_service',
  'saas_ai',
  'regional_service',
  'store_reservation',
  'd2c_product',
  'seminar_event',
  'education_school',
  'recruitment',
  'general_other',
] as const;

export const APPEAL_STYLES = [
  'problem_solution',
  'benefit_future',
  'feature_understanding',
  'trust_relief',
  'story_empathy',
  'offer_push',
  'people_culture',
  'authority_expert',
] as const;

export const INFORMATION_DENSITIES = ['light', 'standard', 'rich'] as const;
export const DEVICE_PRIORITIES = ['balanced', 'mobile_first', 'desktop_first'] as const;
export const VISUAL_TONES = [
  'trust',
  'corporate',
  'friendly',
  'premium',
  'modern',
  'bright_clean',
  'natural_warm',
] as const;
export const MATERIAL_DEPENDENCIES = [
  'text_first',
  'photo_optional',
  'photo_required',
  'ui_capture_required',
  'person_photo_required',
] as const;
export const INTERACTION_LEVELS = [
  'static',
  'light',
  'guided',
  'mobile_conversion_focused',
] as const;
export const ASSET_KINDS = [
  'service_photo',
  'location_photo',
  'ui_capture',
  'team_photo',
  'person_photo',
  'logo',
  'metric',
  'review_source',
  'pricing_source',
  'map_info',
] as const;
export const VISUAL_GENERATION_POLICIES = [
  'none',
  'reuse_only',
  'ai_allowed',
  'diagram_only',
] as const;

export const FAMILY_IDS = [
  'btob_inquiry',
  'saas_ai_inquiry',
  'regional_service_inquiry',
  'store_reservation',
  'd2c_purchase',
  'seminar_application',
  'education_application',
  'recruitment_entry',
] as const;

export const SECTION_TYPES = [
  'hero',
  'problem',
  'solution',
  'benefit',
  'services',
  'features',
  'feature_list',
  'reasons',
  'flow',
  'people',
  'pricing',
  'logo_or_metrics',
  'proof',
  'review',
  'faq',
  'use_cases',
  'comparison',
  'message',
  'target_audience',
  'overview',
  'speaker',
  'instructor',
  'work_style',
  'course_or_curriculum',
  'offer',
  'form',
  'access',
  'cta',
  'sub_cta',
] as const;

export const REGISTRY_STATUSES = ['active', 'draft', 'deprecated'] as const;
export const SECTION_FIELD_KINDS = [
  'text',
  'textarea',
  'rich_text',
  'number',
  'url',
  'image',
  'badge',
  'action',
  'item_list',
  'qa_list',
  'metric_list',
  'person_list',
  'price_list',
  'logo_list',
] as const;
export const GENERATION_FIELD_MODES = [
  'system_locked',
  'ai_generated',
  'hybrid_refine',
] as const;
export const FIELD_SOURCE_REQUIREMENTS = ['none', 'optional', 'verified'] as const;
export const EMPTY_STATE_POLICIES = [
  'fallback_template',
  'omit_optional_fields',
  'omit_section',
  'block_until_user_input',
] as const;
export const THEME_CONTENT_WIDTHS = ['narrow', 'normal', 'wide'] as const;
export const THEME_SECTION_SPACING_BIASES = ['compact', 'balanced', 'spacious'] as const;
export const THEME_BUTTON_STYLES = ['solid', 'outline'] as const;
export const THEME_BORDER_EMPHASES = ['soft', 'normal'] as const;
export const THEME_SHADOW_LEVELS = ['none', 'soft'] as const;
export const THEME_BACKGROUND_TREATMENTS = ['flat', 'subtle_surface'] as const;
export const THEME_PHOTO_USAGES = ['avoid', 'optional', 'recommended', 'hero_dominant'] as const;
export const THEME_DIAGRAM_USAGES = ['avoid', 'optional', 'recommended'] as const;
export const THEME_UI_CAPTURE_USAGES = ['avoid', 'optional', 'recommended'] as const;
export const THEME_CROP_STYLES = ['strict', 'balanced', 'natural'] as const;
export const THEME_EMPHASIS_CHANNELS = ['copy', 'proof', 'visual', 'cta'] as const;
export const MATERIAL_SENSITIVITIES = ['low', 'medium', 'high'] as const;
export const WEAK_MATERIAL_FALLBACK_CONDITIONS = [
  'low_quality_photo',
  'missing_photo',
  'missing_ui_capture',
  'weak_proof',
  'weak_review',
] as const;
export const WEAK_MATERIAL_FALLBACK_ACTIONS = [
  'downgrade_visual_emphasis',
  'prefer_textual_layout',
  'prefer_surface_sections',
  'downgrade_cta_emphasis',
  'switch_theme_candidate',
] as const;
export const CTA_EMPHASIS_LEVELS = ['soft', 'normal', 'strong'] as const;

export const WRITING_STYLES = [
  'formal_desu_masu',
  'soft_desu_masu',
  'premium_terse',
  'modern_concise',
  'warm_conversational',
] as const;

export const HERO_PATTERNS = [
  'problem_first',
  'benefit_first',
  'feature_showcase',
  'trust_authority',
  'offer_urgency',
  'empathy_story',
  'culture_people',
] as const;
export const SOURCE_RECORD_KINDS = [
  ...ASSET_KINDS,
  'review',
  'proof_metric',
  'proof_case',
  'pricing',
  'access',
  'certification',
  'offer',
] as const;
export const SOURCE_AVAILABILITIES = ['available', 'missing', 'unknown'] as const;
export const SOURCE_VERIFICATIONS = ['verified', 'user_claimed', 'unverified', 'unknown'] as const;
export const SOURCE_QUALITIES = ['high', 'medium', 'low', 'unknown'] as const;
export const OVERALL_MATERIAL_QUALITIES = ['high', 'medium', 'low', 'unknown'] as const;
export const PAGE_ROLES = ['landing'] as const;
export const GENERATION_STATUSES = ['ready', 'needs_user_input', 'needs_verified_source'] as const;
export const RENDER_EMPHASIS_LEVELS = ['low', 'normal', 'high'] as const;
export const BACKGROUND_VARIANTS = ['default', 'muted', 'surface'] as const;
export const MOBILE_BEHAVIORS = ['stack', 'carousel', 'sticky_cta', 'accordion'] as const;

export const objectiveTypeSchema = z.enum(OBJECTIVE_TYPES);
export const industryTypeSchema = z.enum(INDUSTRY_TYPES);
export const appealStyleSchema = z.enum(APPEAL_STYLES);
export const informationDensitySchema = z.enum(INFORMATION_DENSITIES);
export const devicePrioritySchema = z.enum(DEVICE_PRIORITIES);
export const visualToneSchema = z.enum(VISUAL_TONES);
export const materialDependencySchema = z.enum(MATERIAL_DEPENDENCIES);
export const interactionLevelSchema = z.enum(INTERACTION_LEVELS);
export const assetKindSchema = z.enum(ASSET_KINDS);
export const visualGenerationPolicySchema = z.enum(VISUAL_GENERATION_POLICIES);
export const familyIdSchema = z.enum(FAMILY_IDS);
export const sectionTypeSchema = z.enum(SECTION_TYPES);
export const registryStatusSchema = z.enum(REGISTRY_STATUSES);
export const sectionFieldKindSchema = z.enum(SECTION_FIELD_KINDS);
export const generationFieldModeSchema = z.enum(GENERATION_FIELD_MODES);
export const fieldSourceRequirementSchema = z.enum(FIELD_SOURCE_REQUIREMENTS);
export const emptyStatePolicySchema = z.enum(EMPTY_STATE_POLICIES);
export const themeContentWidthSchema = z.enum(THEME_CONTENT_WIDTHS);
export const themeSectionSpacingBiasSchema = z.enum(THEME_SECTION_SPACING_BIASES);
export const themeButtonStyleSchema = z.enum(THEME_BUTTON_STYLES);
export const themeBorderEmphasisSchema = z.enum(THEME_BORDER_EMPHASES);
export const themeShadowLevelSchema = z.enum(THEME_SHADOW_LEVELS);
export const themeBackgroundTreatmentSchema = z.enum(THEME_BACKGROUND_TREATMENTS);
export const themePhotoUsageSchema = z.enum(THEME_PHOTO_USAGES);
export const themeDiagramUsageSchema = z.enum(THEME_DIAGRAM_USAGES);
export const themeUiCaptureUsageSchema = z.enum(THEME_UI_CAPTURE_USAGES);
export const themeCropStyleSchema = z.enum(THEME_CROP_STYLES);
export const themeEmphasisChannelSchema = z.enum(THEME_EMPHASIS_CHANNELS);
export const materialSensitivitySchema = z.enum(MATERIAL_SENSITIVITIES);
export const weakMaterialFallbackConditionSchema = z.enum(WEAK_MATERIAL_FALLBACK_CONDITIONS);
export const weakMaterialFallbackActionSchema = z.enum(WEAK_MATERIAL_FALLBACK_ACTIONS);
export const ctaEmphasisLevelSchema = z.enum(CTA_EMPHASIS_LEVELS);
export const writingStyleSchema = z.enum(WRITING_STYLES);
export const heroPatternSchema = z.enum(HERO_PATTERNS);
export const sourceRecordKindSchema = z.enum(SOURCE_RECORD_KINDS);
export const sourceAvailabilitySchema = z.enum(SOURCE_AVAILABILITIES);
export const sourceVerificationSchema = z.enum(SOURCE_VERIFICATIONS);
export const sourceQualitySchema = z.enum(SOURCE_QUALITIES);
export const overallMaterialQualitySchema = z.enum(OVERALL_MATERIAL_QUALITIES);
export const pageRoleSchema = z.enum(PAGE_ROLES);
export const generationStatusSchema = z.enum(GENERATION_STATUSES);
export const renderEmphasisLevelSchema = z.enum(RENDER_EMPHASIS_LEVELS);
export const backgroundVariantSchema = z.enum(BACKGROUND_VARIANTS);
export const mobileBehaviorSchema = z.enum(MOBILE_BEHAVIORS);

export type ObjectiveType = z.infer<typeof objectiveTypeSchema>;
export type IndustryType = z.infer<typeof industryTypeSchema>;
export type AppealStyle = z.infer<typeof appealStyleSchema>;
export type InformationDensity = z.infer<typeof informationDensitySchema>;
export type DevicePriority = z.infer<typeof devicePrioritySchema>;
export type VisualTone = z.infer<typeof visualToneSchema>;
export type MaterialDependency = z.infer<typeof materialDependencySchema>;
export type InteractionLevel = z.infer<typeof interactionLevelSchema>;
export type AssetKind = z.infer<typeof assetKindSchema>;
export type VisualGenerationPolicy = z.infer<typeof visualGenerationPolicySchema>;
export type FamilyId = z.infer<typeof familyIdSchema>;
export type SectionType = z.infer<typeof sectionTypeSchema>;
export type ThemeEmphasisChannel = z.infer<typeof themeEmphasisChannelSchema>;
export type CtaEmphasisLevel = z.infer<typeof ctaEmphasisLevelSchema>;
export type WritingStyle = z.infer<typeof writingStyleSchema>;
export type HeroPattern = z.infer<typeof heroPatternSchema>;

function createClassificationValueSchema<T extends z.ZodType>(valueSchema: T) {
  return z.object({
    value: valueSchema,
    confidence: z.number().min(0).max(1),
    evidence: z.array(z.string().min(1)).default([]),
  });
}

export const classificationValueConfidenceSchema = z.number().min(0).max(1);
export const classificationValueEvidenceSchema = z.array(z.string().min(1)).default([]);

export const sourceInventorySummarySchema = z.object({
  hasServicePhoto: z.boolean(),
  hasUiCapture: z.boolean(),
  hasVerifiedProof: z.boolean(),
  hasVerifiedReview: z.boolean(),
  hasVerifiedPricing: z.boolean(),
  hasVerifiedAccess: z.boolean(),
  overallMaterialQuality: overallMaterialQualitySchema,
});

export const objectiveClassificationSchema = createClassificationValueSchema(objectiveTypeSchema);
export const industryClassificationSchema = createClassificationValueSchema(industryTypeSchema);
export const informationDensityClassificationSchema = createClassificationValueSchema(informationDensitySchema);
export const devicePriorityClassificationSchema = createClassificationValueSchema(devicePrioritySchema);
export const visualToneClassificationSchema = createClassificationValueSchema(visualToneSchema);
export const interactionLevelClassificationSchema = createClassificationValueSchema(interactionLevelSchema);

export const appealStyleClassificationSchema = z.object({
  primary: createClassificationValueSchema(appealStyleSchema),
  secondary: z.array(appealStyleSchema).max(2).default([]),
});

export const materialDependencyClassificationSchema = z.object({
  value: materialDependencySchema,
  confidence: classificationValueConfidenceSchema,
  evidence: classificationValueEvidenceSchema,
  requiredAssetKinds: z.array(assetKindSchema).default([]),
});

export const generationClassificationSchema = z.object({
  objective: objectiveClassificationSchema,
  industry: industryClassificationSchema,
  appealStyle: appealStyleClassificationSchema,
  informationDensity: informationDensityClassificationSchema,
  devicePriority: devicePriorityClassificationSchema,
  visualTone: visualToneClassificationSchema,
  materialDependency: materialDependencyClassificationSchema,
  interactionLevel: interactionLevelClassificationSchema,
  unresolvedQuestions: z.array(z.string().min(1)).default([]),
  safeModeReasons: z.array(z.string().min(1)).default([]),
});

export type ClassificationValue<T extends string> = {
  value: T;
  confidence: number;
  evidence: string[];
};
export type SourceInventorySummary = z.infer<typeof sourceInventorySummarySchema>;
export type GenerationClassification = z.infer<typeof generationClassificationSchema>;

export const registryMetaSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
  version: z.number().int().positive(),
  status: registryStatusSchema,
  priority: z.number().int().nonnegative(),
  tags: z.array(z.string().min(1)).default([]),
});

export const familyRegistrySchema = registryMetaSchema.extend({
  id: familyIdSchema,
  objectiveMatches: z.array(objectiveTypeSchema).min(1),
  industryMatches: z.array(industryTypeSchema).min(1),
  preferredAppealStyles: z.array(appealStyleSchema).default([]),
  avoidedAppealStyles: z.array(appealStyleSchema).default([]),
  requiredSectionTypes: z.array(sectionTypeSchema).min(1),
  recommendedSectionTypes: z.array(sectionTypeSchema).default([]),
  discouragedSectionTypes: z.array(sectionTypeSchema).default([]),
  defaultSkeletonIds: z.array(z.string().min(1)).min(1),
  defaultThemeIds: z.array(z.string().min(1)).default([]),
  notes: z.array(z.string().min(1)).default([]),
});

export const familyRegistryListSchema = z.array(familyRegistrySchema);

export const skeletonRecommendedPlacementSchema = z.enum([
  'hero',
  'middle',
  'footer',
  'mobile_sticky',
]);

export const skeletonDensityPolicySchema = z.object({
  lightMaxOptionalSections: z.number().int().nonnegative(),
  standardMaxOptionalSections: z.number().int().nonnegative(),
  richMaxOptionalSections: z.number().int().nonnegative(),
});

export const skeletonDuplicateRuleSchema = z.object({
  sectionType: sectionTypeSchema,
  maxCount: z.number().int().positive(),
});

export const skeletonCtaPolicySchema = z.object({
  allowInlineSubCta: z.boolean(),
  allowStickyMobileCta: z.boolean(),
  recommendedPlacements: z.array(skeletonRecommendedPlacementSchema).min(1),
});

export const skeletonFormPolicySchema = z.object({
  required: z.boolean(),
  canMergeIntoCta: z.boolean(),
});

export const skeletonCtaPlacementSchema = z.object({
  position: z.string().min(1),
  afterSectionType: sectionTypeSchema.optional(),
  intensity: ctaEmphasisLevelSchema,
  suggestedLabel: z.string().min(1).optional(),
});

export const emotionalRoleSchema = z.object({
  sectionType: sectionTypeSchema,
  emotionalRole: z.string().min(1),
  transitionFrom: z.string().min(1).optional(),
});

export const skeletonRegistrySchema = registryMetaSchema.extend({
  familyId: familyIdSchema,
  requiredSections: z.array(sectionTypeSchema).min(1),
  optionalSections: z.array(sectionTypeSchema).default([]),
  sectionOrder: z.array(sectionTypeSchema).min(1),
  densityPolicy: skeletonDensityPolicySchema,
  duplicateRules: z.array(skeletonDuplicateRuleSchema).default([]),
  ctaPolicy: skeletonCtaPolicySchema,
  formPolicy: skeletonFormPolicySchema,
  emotionalArc: z.string().min(1).optional(),
  ctaPlacements: z.array(skeletonCtaPlacementSchema).default([]),
});

export const skeletonRegistryListSchema = z.array(skeletonRegistrySchema);

export const sectionTemplateFieldSpecSchema = z.object({
  name: z.string().min(1),
  kind: sectionFieldKindSchema,
  required: z.boolean(),
  minItems: z.number().int().positive().optional(),
  maxItems: z.number().int().positive().optional(),
  itemShape: z.record(z.string(), sectionFieldKindSchema).optional(),
  allowAiGeneration: z.boolean(),
  requiresVerifiedSource: z.boolean(),
  sourceKinds: z.array(sourceRecordKindSchema).default([]),
  description: z.string().min(1),
});

export const sectionTemplateAssetRequirementSchema = z.object({
  slotName: z.string().min(1).default('image'),
  assetKind: assetKindSchema,
  required: z.boolean(),
  purpose: z.string().min(1),
  generationPolicy: visualGenerationPolicySchema.default('reuse_only'),
  promptHints: z.array(z.string().min(1)).default([]),
  forbiddenElements: z.array(z.string().min(1)).default([]),
});

export const sectionVisualSlotPlanSchema = z.object({
  slotName: z.string().min(1),
  required: z.boolean(),
  purpose: z.string().min(1),
  sourceKinds: z.array(sourceRecordKindSchema).default([]),
  preferredAssetKinds: z.array(assetKindSchema).default([]),
  generationPolicy: visualGenerationPolicySchema,
  promptHints: z.array(z.string().min(1)).default([]),
  forbiddenElements: z.array(z.string().min(1)).default([]),
});

export const sectionTemplateContentRulesSchema = z.object({
  minItems: z.number().int().positive().optional(),
  maxItems: z.number().int().positive().optional(),
  maxHeadingLength: z.number().int().positive().optional(),
  maxBodyLength: z.number().int().positive().optional(),
  toneNotes: z.array(z.string().min(1)).default([]),
  prohibitedPatterns: z.array(z.string().min(1)).default([]),
});

export const sectionTemplateEmptyStateRulesSchema = z.object({
  policy: emptyStatePolicySchema,
  fallbackTemplateId: z.string().min(1).optional(),
  omitFieldNames: z.array(z.string().min(1)).default([]),
});

export const sectionTemplateRankingSignalsSchema = z.object({
  familyScore: z.number().int().nonnegative(),
  appealScore: z.number().int().nonnegative(),
  deviceScore: z.number().int().nonnegative(),
  densityScore: z.number().int().nonnegative(),
  toneScore: z.number().int().nonnegative(),
  materialScore: z.number().int().nonnegative(),
});

export const sectionTemplateRegistrySchema = registryMetaSchema.extend({
  sectionType: sectionTypeSchema,
  layoutType: z.string().min(1),
  heroPattern: heroPatternSchema.optional(),
  supportedFamilies: z.array(familyIdSchema).min(1),
  supportedAppealStyles: z.array(appealStyleSchema).default([]),
  supportedDevices: z.array(devicePrioritySchema).min(1),
  supportedInformationDensity: z.array(informationDensitySchema).min(1),
  supportedTones: z.array(visualToneSchema).min(1),
  supportedMaterialDependency: z.array(materialDependencySchema).min(1),
  supportedInteractions: z.array(interactionLevelSchema).min(1),
  requiredFields: z.array(sectionTemplateFieldSpecSchema).default([]),
  optionalFields: z.array(sectionTemplateFieldSpecSchema).default([]),
  assetRequirements: z.array(sectionTemplateAssetRequirementSchema).default([]),
  contentRules: sectionTemplateContentRulesSchema,
  emptyStateRules: sectionTemplateEmptyStateRulesSchema,
  rankingSignals: sectionTemplateRankingSignalsSchema,
});

export const sectionTemplateRegistryListSchema = z.array(sectionTemplateRegistrySchema);

export const candidateTraceSchema = z.object({
  candidateId: z.string().min(1),
  score: z.number(),
  droppedReasons: z.array(z.string().min(1)).default([]),
  notes: z.array(z.string().min(1)).default([]),
  selected: z.boolean().default(false),
  fallbackApplied: z.boolean().default(false),
});

export const sectionSelectionTraceSchema = z.object({
  sectionType: sectionTypeSchema,
  templateCandidates: z.array(candidateTraceSchema).default([]),
  selectedTemplateId: z.string().min(1),
  fallbackApplied: z.boolean().default(false),
});

export const selectionTraceSchema = z.object({
  family: z.array(candidateTraceSchema).default([]),
  skeleton: z.array(candidateTraceSchema).default([]),
  theme: z.array(candidateTraceSchema).default([]),
  sections: z.array(sectionSelectionTraceSchema).default([]),
});

export const themeTokensSchema = z.object({
  text: z.string().min(1),
  textMuted: z.string().min(1),
  background: z.string().min(1),
  surface: z.string().min(1),
  surfaceMuted: z.string().min(1),
  border: z.string().min(1),
  primary: z.string().min(1),
  secondary: z.string().min(1),
});

export const themeTypographySchema = z.object({
  headingWeight: z.enum(['bold', 'extrabold']),
  bodyWeight: z.enum(['normal', 'medium']),
  radiusScale: z.enum(['small', 'medium', 'large']),
  spacingScale: z.enum(['compact', 'normal', 'spacious']),
});

export const themeSpacingScaleValuesSchema = z.object({
  sectionPaddingY: z.number().int().positive(),
  sectionGap: z.number().int().positive(),
  stackGap: z.number().int().positive(),
  cardPadding: z.number().int().positive(),
  formFieldHeight: z.number().int().positive(),
});

export const THEME_SPACING_SCALE_VALUES = {
  compact: {
    sectionPaddingY: 64,
    sectionGap: 24,
    stackGap: 20,
    cardPadding: 20,
    formFieldHeight: 48,
  },
  normal: {
    sectionPaddingY: 80,
    sectionGap: 32,
    stackGap: 24,
    cardPadding: 24,
    formFieldHeight: 52,
  },
  spacious: {
    sectionPaddingY: 104,
    sectionGap: 40,
    stackGap: 28,
    cardPadding: 28,
    formFieldHeight: 56,
  },
} as const satisfies Record<z.infer<typeof themeTypographySchema.shape.spacingScale>, z.infer<typeof themeSpacingScaleValuesSchema>>;

export const themeInformationDensityProfileSchema = z.object({
  preferredDensities: z.array(informationDensitySchema).min(1),
  contentWidth: themeContentWidthSchema,
  maxCardsPerRow: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  maxInlineActions: z.union([z.literal(1), z.literal(2)]),
  sectionSpacingBias: themeSectionSpacingBiasSchema,
});

export const themeComponentRulesSchema = z.object({
  buttonStyle: themeButtonStyleSchema,
  borderEmphasis: themeBorderEmphasisSchema,
  shadowLevel: themeShadowLevelSchema,
  backgroundTreatment: themeBackgroundTreatmentSchema,
});

export const themeVisualAssetPolicySchema = z.object({
  photoUsage: themePhotoUsageSchema,
  diagramUsage: themeDiagramUsageSchema,
  uiCaptureUsage: themeUiCaptureUsageSchema,
  cropStyle: themeCropStyleSchema,
  notes: z.array(z.string().min(1)).default([]),
});

export const themeWeakMaterialFallbackSchema = z.object({
  condition: weakMaterialFallbackConditionSchema,
  action: weakMaterialFallbackActionSchema,
  notes: z.array(z.string().min(1)).default([]),
});

export const themeCtaHarmonySchema = z.object({
  maxPrimaryEmphasis: ctaEmphasisLevelSchema,
  allowBannerCta: z.boolean(),
  allowStickyMobileCta: z.boolean(),
  notes: z.array(z.string().min(1)).default([]),
});

export const themeSectionMoodRulesSchema = z.object({
  hero: z.array(z.string().min(1)).default([]),
  proof: z.array(z.string().min(1)).default([]),
  cta: z.array(z.string().min(1)).default([]),
  form: z.array(z.string().min(1)).default([]),
});

export const themeEmphasisPrioritySchema = z
  .array(themeEmphasisChannelSchema)
  .length(4)
  .refine((channels) => new Set(channels).size === channels.length, {
    message: 'emphasisPriority must contain four unique channels.',
  });

export const themeRegistrySchema = registryMetaSchema.extend({
  visualTone: visualToneSchema,
  impressionKeywords: z.array(z.string().min(1)).min(1),
  compatibleFamilies: z.array(familyIdSchema).min(1),
  discouragedFamilies: z.array(familyIdSchema).default([]),
  forbiddenFamilies: z.array(familyIdSchema).default([]),
  fontFamily: z.literal('Noto Sans JP'),
  tokens: themeTokensSchema,
  typography: themeTypographySchema,
  informationDensityProfile: themeInformationDensityProfileSchema,
  componentRules: themeComponentRulesSchema,
  visualAssetPolicy: themeVisualAssetPolicySchema,
  emphasisPriority: themeEmphasisPrioritySchema,
  materialSensitivity: materialSensitivitySchema,
  weakMaterialFallback: z.array(themeWeakMaterialFallbackSchema).default([]),
  ctaHarmony: themeCtaHarmonySchema,
  preferredSectionTemplateIds: z.array(z.string().min(1)).default([]),
  discouragedSectionTemplateIds: z.array(z.string().min(1)).default([]),
  forbiddenSectionTemplateIds: z.array(z.string().min(1)).default([]),
  prohibitedPatterns: z.array(z.string().min(1)).default([]),
  sectionMoodRules: themeSectionMoodRulesSchema,
  usageNotes: z.array(z.string().min(1)).default([]),
});

export const themeRegistryListSchema = z.array(themeRegistrySchema);

export type RegistryMeta = z.infer<typeof registryMetaSchema>;
export type FamilyRegistry = z.infer<typeof familyRegistrySchema>;
export type SkeletonRegistry = z.infer<typeof skeletonRegistrySchema>;
export type SectionTemplateFieldSpec = z.infer<typeof sectionTemplateFieldSpecSchema>;
export type SectionTemplateAssetRequirement = z.infer<typeof sectionTemplateAssetRequirementSchema>;
export type SectionTemplateContentRules = z.infer<typeof sectionTemplateContentRulesSchema>;
export type SectionTemplateEmptyStateRules = z.infer<typeof sectionTemplateEmptyStateRulesSchema>;
export type SectionTemplateRankingSignals = z.infer<typeof sectionTemplateRankingSignalsSchema>;
export type SectionTemplateRegistry = z.infer<typeof sectionTemplateRegistrySchema>;
export type CandidateTrace = z.infer<typeof candidateTraceSchema>;
export type SectionSelectionTrace = z.infer<typeof sectionSelectionTraceSchema>;
export type SelectionTrace = z.infer<typeof selectionTraceSchema>;
export type ThemeTokens = z.infer<typeof themeTokensSchema>;
export type ThemeTypography = z.infer<typeof themeTypographySchema>;
export type ThemeInformationDensityProfile = z.infer<typeof themeInformationDensityProfileSchema>;
export type ThemeComponentRules = z.infer<typeof themeComponentRulesSchema>;
export type ThemeVisualAssetPolicy = z.infer<typeof themeVisualAssetPolicySchema>;
export type ThemeWeakMaterialFallback = z.infer<typeof themeWeakMaterialFallbackSchema>;
export type ThemeCtaHarmony = z.infer<typeof themeCtaHarmonySchema>;
export type ThemeSectionMoodRules = z.infer<typeof themeSectionMoodRulesSchema>;
export type ThemeRegistry = z.infer<typeof themeRegistrySchema>;

export const selectedThemeSnapshotSchema = z.object({
  impressionKeywords: z.array(z.string().min(1)).default([]),
  informationDensityProfile: themeInformationDensityProfileSchema,
  visualAssetPolicy: themeVisualAssetPolicySchema.omit({ notes: true }),
  emphasisPriority: themeEmphasisPrioritySchema,
  ctaHarmony: themeCtaHarmonySchema.omit({ notes: true }),
  preferredSectionTemplateIds: z.array(z.string().min(1)).default([]),
  discouragedSectionTemplateIds: z.array(z.string().min(1)).default([]),
  forbiddenSectionTemplateIds: z.array(z.string().min(1)).default([]),
  prohibitedPatterns: z.array(z.string().min(1)).default([]),
});

export const sourceStructuredItemSchema = z.record(z.string(), z.string());

export const sourceStructuredDataSchema = z.object({
  title: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  role: z.string().min(1).optional(),
  value: z.string().min(1).optional(),
  price: z.string().min(1).optional(),
  body: z.string().min(1).optional(),
  comment: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  accessNote: z.string().min(1).optional(),
  items: z.array(sourceStructuredItemSchema).default([]),
});

export const sourceRecordSchema = z.object({
  id: z.string().min(1),
  kind: sourceRecordKindSchema,
  label: z.string().min(1),
  availability: sourceAvailabilitySchema,
  verification: sourceVerificationSchema,
  quality: sourceQualitySchema,
  sourceRef: z.string().min(1).optional(),
  structuredData: sourceStructuredDataSchema.optional(),
  notes: z.array(z.string().min(1)).default([]),
});

export const generationSourceInventorySchema = z.object({
  records: z.array(sourceRecordSchema).default([]),
  summary: z.object({
    hasVerifiedProof: z.boolean(),
    hasVerifiedReview: z.boolean(),
    hasVerifiedPricing: z.boolean(),
    hasVerifiedAccess: z.boolean(),
    hasUiCapture: z.boolean(),
    hasServicePhoto: z.boolean(),
    overallMaterialQuality: overallMaterialQualitySchema,
  }),
});

export const pageDesignGuardSchema = z.object({
  dominantChannel: themeEmphasisChannelSchema,
  secondaryChannels: z.array(themeEmphasisChannelSchema).default([]),
  ctaIntensity: ctaEmphasisLevelSchema,
  highEmphasisSectionIds: z.array(z.string().min(1)).default([]),
  notes: z.array(z.string().min(1)).default([]),
});

export const sectionSourcePolicySchema = z.object({
  allowAiGeneratedFields: z.array(z.string().min(1)).default([]),
  requiredVerifiedFields: z.array(z.string().min(1)).default([]),
  optionalVerifiedFields: z.array(z.string().min(1)).default([]),
  forbiddenFabricationFields: z.array(z.string().min(1)).default([]),
  sourceNotes: z.array(z.string().min(1)).default([]),
});

export const sectionSourceBindingsSchema = z.object({
  requiredSourceIds: z.array(z.string().min(1)).default([]),
  optionalSourceIds: z.array(z.string().min(1)).default([]),
  missingRequiredKinds: z.array(z.string().min(1)).default([]),
});

export const compositionFieldCopyConstraintSchema = z.object({
  maxHeadingLength: z.number().int().positive().optional(),
  maxBodyLength: z.number().int().positive().optional(),
  tone: z.string().min(1).optional(),
  prohibitedPatterns: z.array(z.string().min(1)).default([]),
});

export const compositionFieldSpecSchema = z.object({
  name: z.string().min(1),
  kind: sectionFieldKindSchema,
  required: z.boolean(),
  generationMode: generationFieldModeSchema,
  sourceRequirement: fieldSourceRequirementSchema,
  minItems: z.number().int().positive().optional(),
  maxItems: z.number().int().positive().optional(),
  itemShape: z.record(z.string(), sectionFieldKindSchema).optional(),
  copyConstraint: compositionFieldCopyConstraintSchema.optional(),
});

export const sectionContentSpecSchema = z.object({
  requiredFields: z.array(compositionFieldSpecSchema).default([]),
  optionalFields: z.array(compositionFieldSpecSchema).default([]),
  copyGoal: z.string().min(1),
  contentRules: z.array(z.string().min(1)).default([]),
});

export const sectionAssetSpecSchema = z.object({
  requiredAssetKinds: z.array(assetKindSchema).default([]),
  optionalAssetKinds: z.array(assetKindSchema).default([]),
  emptyStatePolicy: emptyStatePolicySchema,
  fallbackTemplateId: z.string().min(1).optional(),
  blockingReason: z.string().min(1).optional(),
});

export const sectionRenderHintsSchema = z.object({
  emphasis: renderEmphasisLevelSchema,
  backgroundVariant: backgroundVariantSchema,
  mobileBehavior: mobileBehaviorSchema,
  anchorId: z.string().min(1).optional(),
});

export const systemFieldValueItemSchema = z.record(z.string(), z.string());
export const systemFieldValueSchema = z.union([
  z.string().min(1),
  z.array(systemFieldValueItemSchema),
]);
export const sectionSystemFieldValuesSchema = z.record(
  z.string(),
  systemFieldValueSchema,
);

export const compositionSectionSchema = z.object({
  id: z.string().min(1),
  order: z.number().int().nonnegative(),
  sectionType: sectionTypeSchema,
  templateId: z.string().min(1),
  required: z.boolean(),
  generationStatus: generationStatusSchema,
  sourcePolicy: sectionSourcePolicySchema,
  sourceBindings: sectionSourceBindingsSchema,
  contentSpec: sectionContentSpecSchema,
  assetSpec: sectionAssetSpecSchema,
  visualSlots: z.array(sectionVisualSlotPlanSchema).default([]),
  systemFieldValues: sectionSystemFieldValuesSchema.default({}),
  renderHints: sectionRenderHintsSchema,
});

export const compositionPageSchema = z.object({
  pageId: z.string().min(1),
  pageRole: pageRoleSchema,
  title: z.string().min(1),
  path: z.literal('/'),
  pageDesignGuard: pageDesignGuardSchema,
  sections: z.array(compositionSectionSchema).min(1),
});

export const compositionValidationStateSchema = z.object({
  isValid: z.boolean(),
  errors: z.array(z.string().min(1)).default([]),
  warnings: z.array(z.string().min(1)).default([]),
  blockedByMissingSources: z.array(z.string().min(1)).default([]),
});

export const copyDirectionSchema = z.object({
  tone: visualToneSchema,
  writingStyle: writingStyleSchema,
  targetPersona: z.string().min(1),
  keyMessage: z.string().min(1),
  differentiator: z.string().min(1),
});

export const ctaStrategySchema = z.object({
  primaryAction: z.string().min(1),
  ctaPlacements: z.array(skeletonCtaPlacementSchema),
});

export const pageContextSchema = z.object({
  emotionalArc: z.string().min(1),
  copyDirection: copyDirectionSchema,
  sectionRoles: z.array(emotionalRoleSchema),
  ctaStrategy: ctaStrategySchema,
});

export const copyQualityErrorSchema = z.object({
  sectionId: z.string().min(1),
  fieldName: z.string().min(1),
  rule: z.string().min(1),
  message: z.string().min(1),
});

export const copyQualityValidationSchema = z.object({
  isValid: z.boolean(),
  errors: z.array(copyQualityErrorSchema),
  warnings: z.array(copyQualityErrorSchema),
});

export const pageCompositionJsonSchema = z.object({
  version: z.literal(1),
  siteName: z.string().min(1),
  companyName: z.string(),
  classification: generationClassificationSchema,
  sourceInventory: generationSourceInventorySchema,
  selectedFamilyId: familyIdSchema,
  selectedSkeletonId: z.string().min(1),
  selectedThemeId: z.string().min(1),
  selectedThemeSnapshot: selectedThemeSnapshotSchema,
  selectionTrace: selectionTraceSchema,
  pages: z.array(compositionPageSchema).min(1),
  validation: compositionValidationStateSchema,
  pageContext: pageContextSchema.optional(),
});

export type GenerationSourceInventory = z.infer<typeof generationSourceInventorySchema>;
export type SourceStructuredItem = z.infer<typeof sourceStructuredItemSchema>;
export type SourceStructuredData = z.infer<typeof sourceStructuredDataSchema>;
export type SourceRecord = z.infer<typeof sourceRecordSchema>;
export type SelectedThemeSnapshot = z.infer<typeof selectedThemeSnapshotSchema>;
export type PageDesignGuard = z.infer<typeof pageDesignGuardSchema>;
export type SectionSourcePolicy = z.infer<typeof sectionSourcePolicySchema>;
export type SectionSourceBindings = z.infer<typeof sectionSourceBindingsSchema>;
export type CompositionFieldSpec = z.infer<typeof compositionFieldSpecSchema>;
export type SectionContentSpec = z.infer<typeof sectionContentSpecSchema>;
export type SectionAssetSpec = z.infer<typeof sectionAssetSpecSchema>;
export type SectionVisualSlotPlan = z.infer<typeof sectionVisualSlotPlanSchema>;
export type SectionRenderHints = z.infer<typeof sectionRenderHintsSchema>;
export type SystemFieldValueItem = z.infer<typeof systemFieldValueItemSchema>;
export type SystemFieldValue = z.infer<typeof systemFieldValueSchema>;
export type SectionSystemFieldValues = z.infer<typeof sectionSystemFieldValuesSchema>;
export type CompositionSection = z.infer<typeof compositionSectionSchema>;
export type CompositionPage = z.infer<typeof compositionPageSchema>;
export type CompositionValidationState = z.infer<typeof compositionValidationStateSchema>;
export type SkeletonCtaPlacement = z.infer<typeof skeletonCtaPlacementSchema>;
export type EmotionalRole = z.infer<typeof emotionalRoleSchema>;
export type CopyDirection = z.infer<typeof copyDirectionSchema>;
export type CtaStrategy = z.infer<typeof ctaStrategySchema>;
export type PageContext = z.infer<typeof pageContextSchema>;
export type CopyQualityError = z.infer<typeof copyQualityErrorSchema>;
export type CopyQualityValidation = z.infer<typeof copyQualityValidationSchema>;
export type PageCompositionJson = z.infer<typeof pageCompositionJsonSchema>;

export function buildSelectedThemeSnapshot(theme: ThemeRegistry): SelectedThemeSnapshot {
  return {
    impressionKeywords: [...theme.impressionKeywords],
    informationDensityProfile: { ...theme.informationDensityProfile },
    visualAssetPolicy: {
      photoUsage: theme.visualAssetPolicy.photoUsage,
      diagramUsage: theme.visualAssetPolicy.diagramUsage,
      uiCaptureUsage: theme.visualAssetPolicy.uiCaptureUsage,
      cropStyle: theme.visualAssetPolicy.cropStyle,
    },
    emphasisPriority: [...theme.emphasisPriority],
    ctaHarmony: {
      maxPrimaryEmphasis: theme.ctaHarmony.maxPrimaryEmphasis,
      allowBannerCta: theme.ctaHarmony.allowBannerCta,
      allowStickyMobileCta: theme.ctaHarmony.allowStickyMobileCta,
    },
    preferredSectionTemplateIds: [...theme.preferredSectionTemplateIds],
    discouragedSectionTemplateIds: [...theme.discouragedSectionTemplateIds],
    forbiddenSectionTemplateIds: [...theme.forbiddenSectionTemplateIds],
    prohibitedPatterns: [...theme.prohibitedPatterns],
  };
}
